import React, { useMemo } from 'react';

import { useHistory } from 'react-router';

import { IconButton } from '@material-ui/core';
import MenuItem from '@material-ui/core/MenuItem';

import { UiButton } from './ui-button';
import { SplitButtonRenderProps, UiSplitButton } from './ui-split-button';

import { ICON_NAMES, MaterialDesignIcon } from '../util/material-design-icons';

export type ButtonAction = {
  name: string;
  handler: (e?: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  Icon?: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'tertiary';
  style?: string;
  disabled?: boolean;
};

type InputProps = {
  /**
   * Required: title of the page/section
   * For example: "Surveys" would be the title for the survey page and the survey results page
   * */
  pageTitle: string;
  /**
   * Optional: subtitle of the page
   * For example: "Survey Results" would be the subsection for the survey results page
   * Cannot be provided if subsectionTitleWithSelector is provided
   */
  subsectionTitle?: string;
  /**
   * Optional: subtitle of the page if there are multiple ways to view the same page
   * Currently used for the job selector in job pages
   * Cannot be provided if subsectionTitle is provided
   */
  subsectionTitleWithSelector?: React.ReactElement;
  /**
   * Optional: url to return to if the user clicks the back arrow
   * if this prop is undefined, no back arrow will be show
   */
  backToPage?: string;
  /**
   * Optional: Main page action used to create UiButton or UiSplitButton
   * If multiple actions are provided, the button will be a split button - the first action will be the main action
   */
  primaryActions?: Array<ButtonAction>;
  /**
   * Optional: secondary page action used to create UiButton
   * This option should not be provided if there is no primary page action, but that is not enforced for style flexibility
   */
  secondaryAction?: ButtonAction;
  /**
   * Optional: custom primary action
   * This option is available if you need to pass fully contained components instead of creating a UiButton - helpful for dialogs
   * This option should not be provided if a primary page action is provided
   */
  customPrimaryAction?: JSX.Element | null | undefined;
  /**
   * Optional: custom secondary action
   * This option is available if you need to pass fully contained components instead of creating a UiButton - helpful for dialogs
   * This option should not be provided if a secondary page action is provided,
   */
  customSecondaryAction?: JSX.Element | null | undefined;
  /**
   * Optional:  Action positioned after page title
   * Currently used for "View All" on jobs page and contact card popup on surveys/directory
   */
  titleAction?: ButtonAction;
  /**
   * Optional: Icon button action
   * Positioned on the right with primary and secondary actions
   */
  iconAction?: { handler: () => void; Icon: React.ReactNode };
  /**
   * Optional: Kebab menu and items positioned after primary action
   */
  kebabMenu?: React.ReactNode;
  /**
   * Optional: Class names to override styling
   * Should be used hesitantly, currently only to increase header height on Performance Chart page
   */
  className?: string;
};

const MainActionButton = ({
  openClass,
  disabled,
  primaryActions,
}: SplitButtonRenderProps & { primaryActions: Array<ButtonAction> }) => {
  if (!primaryActions) {
    return <div />;
  }

  const mainAction = primaryActions[0];
  return (
    <UiButton
      size="sm"
      className={`u-borderRadius-right-none u-border-right u-boxShadow-none ${openClass} u-height-headerButton`}
      variant="primary"
      disabled={disabled || mainAction.disabled}
      onClick={mainAction.handler}
      startIcon={mainAction.Icon}
    >
      {mainAction.name}
    </UiButton>
  );
};
const DropdownActionButtons = ({ primaryActions }: { primaryActions: Array<ButtonAction> }) => {
  if (!primaryActions) {
    return <div />;
  }
  const dropdownActions = [...primaryActions.slice(1)];
  return (
    <>
      {dropdownActions.map((action) => (
        <MenuItem onClick={action.handler}>{action.name}</MenuItem>
      ))}
    </>
  );
};

const PrimaryActionButton = ({ primaryActions }: { primaryActions: Array<ButtonAction> }) => {
  if (!primaryActions || !primaryActions.length) {
    return null;
  }

  const useSplitButton = primaryActions.length > 1;
  const mainAction = primaryActions[0];

  const MainAction = (splitButtonProps: SplitButtonRenderProps) =>
    useMemo(
      () => <MainActionButton primaryActions={primaryActions} {...splitButtonProps} />,
      [splitButtonProps],
    );

  const DropdownActions = (splitButtonProps: SplitButtonRenderProps) =>
    useMemo(
      () => <DropdownActionButtons primaryActions={primaryActions} {...splitButtonProps} />,
      [splitButtonProps],
    );

  return useSplitButton ? (
    <UiSplitButton
      MainButton={MainAction}
      DropdownButtons={DropdownActions}
      popperButtonSize="sm"
      disabled={mainAction?.disabled}
    />
  ) : (
    <UiButton
      size="sm"
      className={`u-height-headerButton ${mainAction.style}`}
      onClick={mainAction.handler}
      variant={mainAction.variant || 'primary'}
      startIcon={mainAction.Icon}
      disabled={mainAction.disabled}
    >
      {mainAction.name}
    </UiButton>
  );
};

export const UiBreadcrumbHeader = ({
  pageTitle,
  subsectionTitle,
  subsectionTitleWithSelector,
  backToPage,
  primaryActions,
  secondaryAction,
  customPrimaryAction,
  customSecondaryAction,
  titleAction,
  iconAction,
  kebabMenu,
  className,
}: InputProps) => {
  const history = useHistory();

  if (subsectionTitle && subsectionTitleWithSelector) {
    throw new Error(
      'Invalid configuration - cannot provide both subsection title and subsection title with selector',
    );
  }
  const isSubsectionProvided = subsectionTitle || subsectionTitleWithSelector;
  const pageTitleClass = isSubsectionProvided ? 'typo-h1--secondary' : 'typo-h1';
  const subsectionTitleClass = subsectionTitleWithSelector ? 'typo-headerLink--large' : 'typo-h1';

  const handleNavigateBack = () => (backToPage ? history.push(backToPage) : null);
  const hasActionsProvided =
    primaryActions || secondaryAction || customPrimaryAction || customSecondaryAction || kebabMenu;
  const hasFullActionSet =
    (primaryActions || customPrimaryAction) && (secondaryAction || customSecondaryAction);

  return (
    <div
      className={`${
        className || ''
      } u-spread u-position-top-sticky u-background-lighterGray u-padding-y u-padding-x-xl
      u-border-bottom u-alignItems-center u-width-100 breadcrumbHeader-height`}
    >
      <div className="u-display-flex u-alignItems-center u-overflow-hidden u-whiteSpace-nowrap">
        {backToPage && (
          <div
            className="typo-headerLink u-margin-right u-height-25px"
            role="presentation"
            onClick={handleNavigateBack}
          >
            <MaterialDesignIcon iconName={ICON_NAMES.ARROW_BACK} size="md" />
          </div>
        )}
        <div className={`${pageTitleClass} u-overflow-hidden u-textOverflow-ellipsis`}>
          {pageTitle}
        </div>
        {isSubsectionProvided && (
          <>
            <div className={`u-margin-x-sm ${pageTitleClass}`}>/</div>
            <div className={subsectionTitleClass}>
              {subsectionTitle || subsectionTitleWithSelector}
            </div>
          </>
        )}
        {titleAction && (
          <div
            className="typo-headerLink u-margin-left-xl u-margin-top-sm u-flex-center"
            role="presentation"
            onClick={titleAction.handler}
          >
            {titleAction.Icon}
            {titleAction.name}
          </div>
        )}
      </div>
      {hasActionsProvided && (
        <div className="u-flex-center u-margin-x">
          {iconAction && (
            <IconButton className="u-backgroundHover-transparent" onClick={iconAction.handler}>
              {iconAction.Icon}
            </IconButton>
          )}
          {secondaryAction && (
            <UiButton
              size="sm"
              className={`u-height-headerButton ${secondaryAction.style}`}
              variant={secondaryAction.variant || 'tertiary'}
              onClick={secondaryAction.handler}
              startIcon={secondaryAction.Icon}
              disabled={secondaryAction.disabled}
            >
              {secondaryAction.name}
            </UiButton>
          )}
          {customSecondaryAction}
          <div className={hasFullActionSet ? 'u-margin-left' : ''}>
            {customPrimaryAction}
            {primaryActions && <PrimaryActionButton primaryActions={primaryActions} />}
          </div>
          {kebabMenu ? <div className="u-margin-left">{kebabMenu}</div> : null}
        </div>
      )}
    </div>
  );
};
